import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { api, setAccessToken, setAuthCallbacks } from '../api/client';
import type { User } from '@spirtech/shared';

/*
   Auth context — passwordless flow + rotating refresh tokens.

   Lifecycle:
   - On mount, attempt a silent refresh by calling POST /auth/refresh. The
     refresh cookie (rt) is httpOnly, so success means the browser still has
     a valid long-lived session; the server returns a fresh access token and
     the user payload.
   - verifyCode(email, code) exchanges the 6-digit code for an access token
     pair (token + user) and seeds the in-memory access token.
   - logout calls /auth/logout (revokes the rt server-side, clears the cookie),
     then drops the in-memory token. The 401-retry interceptor in api/client.ts
     handles transparent rotation as the access token expires.
*/

interface VerifyCodeResponse {
	accessToken: string;
	user: User;
}

interface AuthState {
	user: User | null;
	loading: boolean;
	requestCode: (email: string) => Promise<void>;
	verifyCode: (email: string, code: string) => Promise<void>;
	logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);
	const fBootstrapped = useRef(false);

	// Wire api/client callbacks: when the 401-retry interceptor refreshes the
	// access token transparently, AuthContext doesn't need to do anything (the
	// user remains the same). When the refresh itself fails, we drop the user
	// so React Router routes back to /login.
	useEffect(() => {
		setAuthCallbacks(
			() => {
				/* nothing — token already stashed by the interceptor */
			},
			() => {
				setUser(null);
				setAccessToken(null);
			},
		);
	}, []);

	// Silent bootstrap — the refresh cookie may carry a still-valid session.
	// If yes, we land directly on the protected area; if no, the user payload
	// stays null and the app routes to /login.
	useEffect(() => {
		if (fBootstrapped.current) return;
		fBootstrapped.current = true;
		(async () => {
			try {
				const res = await fetch('/api/auth/refresh', {
					method: 'POST',
					credentials: 'include',
					headers: { 'X-Requested-With': 'XMLHttpRequest' },
				});
				if (res.ok) {
					const body = (await res.json()) as VerifyCodeResponse;
					setAccessToken(body.accessToken);
					setUser(body.user);
				}
			} catch {
				// Network errors land here — leave the user unauthenticated.
			} finally {
				setLoading(false);
			}
		})();
	}, []);

	async function requestCode(email: string) {
		await api.post('/auth/request-code', { email });
	}

	async function verifyCode(email: string, code: string) {
		const body = await api.post<VerifyCodeResponse>('/auth/verify-code', { email, code });
		setAccessToken(body.accessToken);
		setUser(body.user);
	}

	async function logout() {
		try {
			await api.post('/auth/logout', {});
		} catch {
			// Even if the network call fails, drop local state — the SPA must end up
			// at /login regardless of the server's reachability.
		}
		setAccessToken(null);
		setUser(null);
	}

	return (
		<AuthContext.Provider value={{ user, loading, requestCode, verifyCode, logout }}>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth(): AuthState {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
	return ctx;
}
